import { useEffect, useState } from 'react'

import screenshotClientMangement from '#app/images/screenshots/client_management.png'
import screenshotTimelines from '#app/images/screenshots/event_timeline.png'
import screenshotEventVenues from '#app/images/screenshots/event_venues.png'
import { cn } from '#app/utils/misc.tsx'
import {
	Carousel,
	CarouselContent,
	CarouselItem,
	type CarouselApi,
} from './ui/carousel'
import { Icon } from './ui/icon'
import { Tabs, TabsList, TabsTrigger } from './ui/tabs'

type ImageProps = Omit<
	JSX.IntrinsicElements['img'],
	'src' | 'srcSet' | 'ref' | 'alt' | 'width' | 'height' | 'loading'
> & {
	src: string
	alt: string
	width?: number | `${number}`
	height?: number | `${number}`
	fill?: boolean
	quality?: number | `${number}`
	priority?: boolean
	placeholder?: string
	blurDataURL?: string
	unoptimized?: boolean

	lazyRoot?: string
}

interface Feature {
	name: React.ReactNode
	summary: string
	description: string
	image: ImageProps['src']
	icon: React.ComponentType
}

const features: Array<Feature> = [
	{
		name: 'Client Management',
		summary: 'Unlock Exceptional Client Experiences.',
		description:
			'From the initial spark of an idea to the final applause, our app seamlessly connects planners with clients. This journey transforms event planning into a collaborative adventure, filled with creativity and success, ensuring an unforgettable client experience.',
		image: screenshotClientMangement,
		icon: function ClientsIcon() {
			return <Icon className="text-white" name="avatar"></Icon>
		},
	},
	{
		name: 'Event Venues',
		summary: 'Sophistication Meets Coordination',
		description: `Adjust and refine your event's details with ease. Our app synchronizes changes to your event timeline with venue-related tasks in real-time, ensuring seamless communication and coordination across all parties.`,
		image: screenshotEventVenues,
		icon: function EventsIcon() {
			return <Icon className="text-white" name="home"></Icon>
		},
	},
	{
		name: 'Timelines',
		summary: 'Navigating Event Planning with Precision.',
		description:
			'Timelines are your anchor in the dynamic seas of event planning; maintaining a clear, detailed timeline is crucial. We encourage this practice as it promotes organization and professionalism, enhancing your ability to manage any event with confidence.',
		image: screenshotTimelines,
		icon: function ContactsIcon() {
			return <Icon className="text-white" name="card-stack"></Icon>
		},
	},
]

function Feature({
	feature,
	isActive,
	className,
	...props
}: React.ComponentPropsWithoutRef<'div'> & {
	feature: Feature
	isActive: boolean
}) {
	return (
		<div
			className={cn(className, !isActive && 'opacity-75 hover:opacity-100')}
			{...props}
		>
			<div
				className={cn(
					'w-9 rounded-lg',
					isActive ? 'bg-emerald-600' : 'bg-slate-500',
				)}
			>
				<svg aria-hidden="true" className="h-9 w-9 p-1" fill="none">
					<feature.icon />
				</svg>
			</div>
			<h3
				className={cn(
					'mt-6 text-sm font-medium',
					isActive ? 'text-emerald-600' : 'text-slate-600 dark:text-white',
				)}
			>
				{feature.name}
			</h3>
			<p className="font-display mt-2 text-xl text-slate-900 dark:text-white">
				{feature.summary}
			</p>
			<p className="mt-4 text-sm text-slate-600 dark:text-white">
				{feature.description}
			</p>
		</div>
	)
}

export function FeaturesMobile() {
	return (
		<div className="-mx-4 mt-20 flex flex-col gap-y-10 overflow-hidden px-4 sm:-mx-6 sm:px-6 lg:hidden">
			{features.map(feature => (
				<div key={feature.summary}>
					<Feature feature={feature} className="mx-auto max-w-2xl" isActive />
					<div className="relative mt-10 pb-10">
						<div className="absolute -inset-x-4 bottom-0 top-8 bg-slate-200 sm:-inset-x-6" />
						<div className="relative mx-auto w-[52.75rem] overflow-hidden rounded-xl bg-white shadow-lg shadow-slate-900/5 ring-1 ring-slate-500/10 dark:bg-background">
							<img
								className="w-full"
								src={feature.image}
								alt=""
								sizes="52.75rem"
							/>
						</div>
					</div>
				</div>
			))}
		</div>
	)
}

export function FeaturesDesktop() {
	const [activeTab, setActiveTab] = useState(0)

	const [api, setApi] = useState<CarouselApi>()
	const [current, setCurrent] = useState(0)
	const [count, setCount] = useState(0)

	useEffect(() => {
		if (api && typeof activeTab === 'number') {
			api.scrollTo(activeTab)
		}
	}, [activeTab, api])

	useEffect(() => {
		if (!api) return

		setCount(api.scrollSnapList().length)
		setCurrent(api.selectedScrollSnap() + 1)

		const updateCurrent = () => {
			setCurrent(api.selectedScrollSnap() + 1)
			setActiveTab(api.selectedScrollSnap()) // Update tab based on carousel slide
		}

		api.on('select', updateCurrent)

		return () => {
			api.off('select', updateCurrent)
		}
	}, [api])

	const handleTabChange = (name: string) => {
		const newIndex = features.findIndex(feature => feature.name === name)
		setActiveTab(newIndex)
	}

	return (
		<Tabs
			defaultValue="Client Management"
			className="hidden lg:mt-20 lg:block"
			onValueChange={handleTabChange}
		>
			<TabsList className="h-22 grid grid-cols-3 gap-x-8 bg-inherit p-8">
				{features.map((feature, featureIndex) => (
					<Feature
						key={feature.summary}
						feature={{
							...feature,
							name: (
								<TabsTrigger
									value={`${feature.name}`}
									className="[&:not(:focus-visible)]:focus:outline-none"
								>
									<span className="absolute inset-0" />
									{feature.name}
								</TabsTrigger>
							),
						}}
						isActive={activeTab === featureIndex}
						className="relative"
					/>
				))}
			</TabsList>

			<div className="mt-20 flex">
				<Carousel setApi={setApi} className="mx-auto">
					<CarouselContent>
						{features.map(feature => (
							<CarouselItem key={`${feature.name}`}>
								<img
									src={feature.image}
									alt={feature.description}
									className="h-auto w-full rounded-xl"
								/>
								{/* <div className="mt-2 text-center">{feature.description}</div> */}
							</CarouselItem>
						))}
					</CarouselContent>
				</Carousel>
			</div>
			<div className="py-2 text-center text-sm text-muted-foreground">
				Slide {current} of {count}
			</div>
		</Tabs>
	)
}
